import type Owner from '@ember/owner';
import { LinkTo } from '@ember/routing';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { sortBy } from 'webapp/utils/common';

import BarLoaderComponent from 'webapp/components/bar-loader';
import CustomQueryGridItemComponent from 'webapp/components/custom-query/grid-item';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import ScriptsGridItemComponent from 'webapp/components/scripts/grid-item';

import type { Metric } from 'webapp/models-custom';

export default class CustomMetricsPopupPromoComponent extends PopupInnerModalComponent<PopupData.None> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked isLoading;

	origin = 'custom_metrics_promo_popup';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.isLoading = true;
		void this.mmMetricsService
			.prepareForMetricEditing()
			.then(() => {
				next(this.scheduleReposition);
			})
			.finally(() => {
				this.isLoading = false;
			});

		this.analyticsService.event(TrackingEvent.CUSTOM_CALCULATED_METRIC_PROMO_SHOWN, {
			origin: this.origin,
		});

		next(this, () => {
			void this.userSettingsService.save(
				UserSettingsKey.CUSTOM_METRICS_PROMO_POPUP_SEEN,
				true,
			);
		});
	}

	get metrics(): Metric[] {
		const customQueryMetrics = sortBy(this.mmMetricsService.customMetrics, 'usedCount')
			.reverse()
			.slice(0, 6);

		const scriptMetrics = sortBy(this.mmMetricsService.calculatedMetrics, 'usedCount')
			.reverse()
			.slice(0, 6);

		return sortBy([...customQueryMetrics, ...scriptMetrics], 'usedCount').reverse();
	}
	<template>
		<div class="title title--vertical">
			<img src="/images/svg/calculator.svg" height="48" width="48" alt="Header" />
			<span class="text">Discover deeper insights with custom metrics</span>
			<span class="title__subtext">
				With a single click create powerful metrics and gain advanced insights into your
				performance
			</span>
		</div>
		<div class="content">

			{{#if this.isLoading}}
				<div class="loader-ct">
					<BarLoaderComponent />
				</div>
			{{else}}
				<div class="section section--metrics">
					<div class="section__header">
						<p>Most popular custom metrics</p>
						<LinkTo @route="metric-library">Browse all metrics &gt;</LinkTo>
					</div>
					<ul class="flex-ct flex-ct--size-small-block-metric">
						{{#each this.metrics as |metric|}}
							{{#if metric.isTypeCustomQueryOrGeneric}}
								<CustomQueryGridItemComponent
									@model={{metric}}
									@trackingOriginForAdd={{this.origin}}
								/>
							{{else}}
								<ScriptsGridItemComponent
									@model={{metric}}
									@trackingOriginForAdd={{this.origin}}
								/>
							{{/if}}
						{{/each}}
						{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
						<li class="flex-item flex-item--fake"></li>
						<li class="flex-item flex-item--fake"></li>
					</ul>
				</div>
			{{/if}}

		</div>
	</template>
}
